import { defineStore } from 'pinia';
import { compressSnapshot, decompressSnapshot, formatBytes } from './bookmarkCompression.js';

const STORAGE_WARN_BYTES = 10 * 1024 * 1024;      // 10 MB
const STORAGE_WARN_RATIO = 10;                      // 10× current design

export const useBookmarkStore = defineStore('bookmarks', {
    state: () => ({
        bookmarks: [],  // DesignBookmark[]
    }),

    getters: {
        totalCompressedBytes(state) {
            return state.bookmarks.reduce((sum, b) => sum + b.compressedSizeBytes, 0);
        },
        totalCompressedFormatted() {
            return formatBytes(this.totalCompressedBytes);
        },
        storageWarning: (state) => (currentDesignBytes) => {
            const total = state.bookmarks.reduce((sum, b) => sum + b.compressedSizeBytes, 0);
            if (total > STORAGE_WARN_BYTES) return `Bookmarks exceed 10 MB total storage.`;
            if (currentDesignBytes > 0 && total > currentDesignBytes * STORAGE_WARN_RATIO)
                return `Bookmarks are ${Math.round(total / currentDesignBytes)}× larger than the current design.`;
            return null;
        },
    },

    actions: {
        /**
         * Create a new bookmark from a raw design snapshot string.
         * Returns the created bookmark.
         */
        addBookmark(name, rawSnapshot, description = '') {
            const { compressedSnapshot, compressed, originalSizeBytes, compressedSizeBytes, compressionRatio } =
                compressSnapshot(rawSnapshot);
            const bookmark = {
                id: crypto.randomUUID(),
                name,
                timestamp: Date.now(),
                description,
                compressed,
                originalSizeBytes,
                compressedSizeBytes,
                compressionRatio,
                protected: false,
                compressedSnapshot,
            };
            this.bookmarks.push(bookmark);
            return bookmark;
        },

        /** Decompress and return the raw snapshot for a given bookmark id. */
        getSnapshot(id) {
            const b = this.bookmarks.find(b => b.id === id);
            if (!b) return null;
            return decompressSnapshot(b.compressedSnapshot);
        },

        renameBookmark(id, newName) {
            const b = this.bookmarks.find(b => b.id === id);
            if (b) b.name = newName;
        },

        setDescription(id, description) {
            const b = this.bookmarks.find(b => b.id === id);
            if (b) b.description = description;
        },

        setProtected(id, value) {
            const b = this.bookmarks.find(b => b.id === id);
            if (b) b.protected = value;
        },

        deleteBookmark(id) {
            const b = this.bookmarks.find(b => b.id === id);
            if (b?.protected) return false;
            this.bookmarks = this.bookmarks.filter(b => b.id !== id);
            return true;
        },

        /** Delete all non-protected bookmarks older than `days` days. */
        deleteOlderThan(days) {
            const cutoff = Date.now() - days * 86400 * 1000;
            this.bookmarks = this.bookmarks.filter(b => b.protected || b.timestamp >= cutoff);
        },

        /** Keep only the newest `n` bookmarks (protected bookmarks are always kept). */
        keepNewest(n) {
            const unprotected = this.bookmarks
                .filter(b => !b.protected)
                .sort((a, b) => b.timestamp - a.timestamp);
            const toDelete = new Set(unprotected.slice(n).map(b => b.id));
            this.bookmarks = this.bookmarks.filter(b => !toDelete.has(b.id));
        },

        /** Hydrate from persisted plain objects (loaded from project file). */
        loadFromProject(bookmarksData) {
            this.bookmarks = bookmarksData ?? [];
        },

        /** Serialise for writing into the project file. */
        toProjectData() {
            // Exclude nothing — every field is needed for round-trip restore
            return this.bookmarks.map(b => ({ ...b }));
        },

        clear() {
            this.bookmarks = [];
        },
    },
});
